import * as React from 'react';
import { useState, useCallback, memo } from 'react';
import { GetImageUrl, type ImageVariant, isNewUploadPath, buildSrcSet, getLqipUrl } from '../utils';
import { Avatar, AvatarProps } from '../ui';

/** Intrinsic dimensions per variant — used for HTML width/height to prevent CLS */
const VARIANT_DIMENSIONS: Record<string, { width: number; height: number }> = {
	thumb: { width: 200, height: 150 },
	medium: { width: 600, height: 450 },
	large: { width: 1200, height: 900 },
};
const DEFAULT_DIMENSIONS = { width: 1920, height: 1440 };

interface Props {
	/** The relative image name with extension */
	src: string
	/** Request a thumbnail variant (thumb=200px, medium=600px, large=1200px) */
	variant?: ImageVariant
	/** CSS aspect-ratio value to reserve space and prevent CLS (e.g. "4/3", "1/1") */
	aspectRatio?: string
	/** Mark as high-priority LCP image — disables lazy loading and sets fetchpriority="high" */
	priority?: boolean
	/** Responsive sizes hint for srcset (default: responsive grid) */
	sizes?: string
}

/** A standardised image element for displaying image linked from the database */
export const DBImage = memo(function DBImage(props: Props & React.ImgHTMLAttributes<HTMLImageElement>) {
	const { src, variant, aspectRatio, priority, sizes, style, ...imgProps } = props;
	const [error, setError] = useState(false);
	const [loaded, setLoaded] = useState(false);

	const handleError = useCallback(() => setError(true), []);
	const handleLoad = useCallback(() => setLoaded(true), []);

	const mergedStyle: React.CSSProperties = {
		...(aspectRatio ? { aspectRatio } : {}),
		...style,
	};

	if (error) {
		return (
			<img
				{...imgProps}
				style={{ ...mergedStyle, padding: 30, objectFit: 'contain' }}
				src="/static/database/icons/app/logo.png"
				alt={props.alt || ''}
			/>
		);
	}

	const dims = variant ? VARIANT_DIMENSIONS[variant] : DEFAULT_DIMENSIONS;
	const imgSrc = GetImageUrl(src, undefined, variant);
	const useResponsive = !variant && isNewUploadPath(src);

	// LQIP: only for responsive (non-variant) new uploads — variants are small enough
	const lqipUrl = useResponsive ? getLqipUrl(src) : undefined;

	// Loading state styles:
	// - New uploads with LQIP: show blurred placeholder as background
	// - All images: subtle background color until loaded
	const loadingStyle: React.CSSProperties = !loaded
		? lqipUrl
			? { backgroundImage: `url(${lqipUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
			: { backgroundColor: 'var(--color-surface-raised, #f0f0f0)' }
		: {};

	const imgElement = (
		<img
			width={dims?.width}
			height={dims?.height}
			{...imgProps}
			style={{ ...mergedStyle, ...loadingStyle, transition: 'opacity 0.3s ease', opacity: loaded ? 1 : 0 }}
			src={imgSrc}
			alt={props.alt || ''}
			loading={priority ? undefined : 'lazy'}
			decoding="async"
			fetchPriority={priority ? 'high' : undefined}
			{...(useResponsive ? { srcSet: buildSrcSet(src, 'webp'), sizes: sizes || '(max-width: 640px) 50vw, 25vw' } : {})}
			onLoad={handleLoad}
			onError={handleError}
		/>
	);

	// For new uploads without a specific variant, use <picture> for AVIF format negotiation
	if (useResponsive) {
		const avifSrcSet = buildSrcSet(src, 'avif');
		return (
			<picture style={{ display: 'contents' }}>
				{avifSrcSet && <source type="image/avif" srcSet={avifSrcSet} sizes={sizes || '(max-width: 640px) 50vw, 25vw'} />}
				{imgElement}
			</picture>
		);
	}

	return imgElement;
});

/** A standardised image element for displaying image linked from the database */
export function StaticBackground(props: Props & React.ImgHTMLAttributes<HTMLImageElement>) {
	const [loaded, setLoaded] = useState(false);

	return (
		<div>
			{loaded ? null :
				<div className="flex items-center justify-center relative top-11.25">
					<div className="w-8 h-8 bg-surface-raised animate-pulse rounded" />
				</div>
			}
			<img
				style={loaded ? {} : { display: 'none' }}
				{...props}
				src={props.src}
				onLoad={() => setLoaded(true)}
			/>
		</div>
	);
}

/** A standardised image avatar element for displaying image linked from the database */
export const DBAvatar = memo(function DBAvatar(props: Props & AvatarProps) {
	return (
		<Avatar {...props} src={GetImageUrl(props.src)} onError={() => true} />
	);
});
